import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Tooltip } from "@material-ui/core";

import ContactAvatar from "../ContactAvatar";
import ContactStoriesViewer from "../ContactStoriesViewer";
import { i18n } from "../../translate/i18n";
import { readSeenStories, STORIES_SEEN_EVENT } from "../../helpers/contactWhatsappStatus";

const UNSEEN_COLOR = "#25D366";
const SEEN_COLOR = "#B0B7BD";
// Vão entre os segmentos do anel, em graus (como no WhatsApp).
const GAP_DEG = 6;

const useStyles = makeStyles(() => ({
	wrapper: {
		position: "relative",
		display: "inline-flex",
		borderRadius: "50%",
		cursor: "pointer",
	},
	// O anel fica POR FORA do avatar (inset negativo) para não mudar a altura
	// do cabeçalho compacto do ticket.
	ring: {
		position: "absolute",
		inset: -4,
		borderRadius: "50%",
		pointerEvents: "none",
		WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px))",
		mask: "radial-gradient(farthest-side, transparent calc(100% - 2.5px), #000 calc(100% - 2px))",
	},
}));

const ringGradient = (stories, seen) => {
	if (stories.length === 1) {
		return seen[stories[0].id] ? SEEN_COLOR : UNSEEN_COLOR;
	}
	const slice = 360 / stories.length;
	const parts = stories.map((story, index) => {
		const color = seen[story.id] ? SEEN_COLOR : UNSEEN_COLOR;
		const start = index * slice + GAP_DEG / 2;
		const end = (index + 1) * slice - GAP_DEG / 2;
		return `transparent ${index * slice}deg ${start}deg, ${color} ${start}deg ${end}deg, transparent ${end}deg ${(index + 1) * slice}deg`;
	});
	return `conic-gradient(${parts.join(", ")})`;
};

/**
 * Avatar do contato com o anel dos status de 24h (verde = não visto neste
 * navegador, cinza = visto). Clicar no anel abre os stories e NÃO dispara o
 * onClick do cabeçalho (que abre a gaveta do contato).
 */
const ContactStatusAvatar = ({ contact, stories, ticketId, style }) => {
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const [seen, setSeen] = useState(readSeenStories);

	useEffect(() => {
		const refresh = () => setSeen(readSeenStories());
		window.addEventListener(STORIES_SEEN_EVENT, refresh);
		return () => window.removeEventListener(STORIES_SEEN_EVENT, refresh);
	}, []);

	const list = useMemo(() => stories || [], [stories]);

	if (list.length === 0) {
		return <ContactAvatar contact={contact} style={style} />;
	}

	const handleOpen = event => {
		event.stopPropagation();
		setOpen(true);
	};

	return (
		<>
			<Tooltip title={i18n.t("contactWhatsappStatus.stories.open")}>
				<span
					className={classes.wrapper}
					onClick={handleOpen}
					role="button"
					tabIndex={0}
					onKeyDown={event => {
						if (event.key === "Enter" || event.key === " ") handleOpen(event);
					}}
					aria-label={i18n.t("contactWhatsappStatus.stories.open")}
				>
					<ContactAvatar contact={contact} style={style} />
					<span className={classes.ring} style={{ background: ringGradient(list, seen) }} />
				</span>
			</Tooltip>
			{open && (
				<ContactStoriesViewer
					open={open}
					onClose={() => setOpen(false)}
					stories={list}
					contact={contact}
					ticketId={ticketId}
				/>
			)}
		</>
	);
};

export default ContactStatusAvatar;
